import { useContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { QueryClientContext } from "@tanstack/react-query";
import { useAuth } from "./AuthProvider";
import { setMustChangePassword } from "./firstLogin";
import Spinner from "../../school_panel/components/ui/Spinner";

// Long enough to read the dialog, short enough not to feel slow.
export const LOGOUT_DELAY_MS = 900;

// A blocking "Logging you out…" dialog: no close button, Escape and Tab do nothing,
// and the page behind cannot be clicked until the session is cleared and /login opens.
const Logout = () => {
  const { setToken, setRole, setUsername } = useAuth();
  const navigate = useNavigate();
  const queryClient = useContext(QueryClientContext);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    cardRef.current?.focus();
    const block = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Tab") { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("keydown", block, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const timer = window.setTimeout(() => {
      try { localStorage.removeItem("auth_role"); } catch {}
      try { localStorage.removeItem("auth_username"); } catch {}
      setMustChangePassword(false);
      setRole(null as any);
      setUsername(null);
      setToken(null);
      // Nothing from this account stays cached for whoever signs in next.
      queryClient?.clear();
      navigate("/login", { replace: true });
    }, LOGOUT_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", block, true);
      document.body.style.overflow = prevOverflow;
    };
    // Runs once per visit to /logout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9999] grid place-items-center bg-navy/[.38] px-4 backdrop-blur-[2px]">
      <div ref={cardRef} tabIndex={-1} role="alertdialog" aria-modal="true" aria-busy="true"
        aria-labelledby="logout-title" aria-describedby="logout-body"
        className="w-full max-w-[320px] rounded-[20px] bg-surface px-6 py-7 text-center text-ink shadow-card focus:outline-none">
        <Spinner size="md" className="mx-auto text-accent" />
        <h2 id="logout-title" className="mt-4 font-display text-[17px] font-bold">Logging you out…</h2>
        <p id="logout-body" className="mt-1 text-[13px] text-muted">This only takes a moment.</p>
      </div>
    </div>,
    document.body,
  );
};

export default Logout;
